import type { PropsWithChildren, ReactNode } from 'react';
import { createElement, memo } from 'react';

import classNames from 'classnames';

import styles from './PageSection.module.scss';

interface PageSectionAppearance {
  spaceBefore?: boolean;
}

type PageSectionVariant = 'primary' | 'secondary' | 'tertiary';

type HeaderLevel = 1 | 2 | 3 | 4 | 5 | 6;

const VARIANT_TO_HEADER_COMPONENT_MAPPING: Record<PageSectionVariant, `h${HeaderLevel}`> = {
  primary: 'h2',
  secondary: 'h3',
  tertiary: 'h5',
} as const;

interface PageSectionProps {
  id?: string;
  title?: string;
  variant?: PageSectionVariant;
  controls?: ReactNode;
  appearance?: PageSectionAppearance;
  breakout?: boolean;
}

const PageSection = memo(
  ({
    id,
    title,
    controls,
    variant = 'primary',
    appearance,
    breakout,
    children,
  }: PropsWithChildren<PageSectionProps>) => {
    return (
      <section
        id={id}
        className={styles['self']}
        data-space-before={appearance?.spaceBefore}
      >
        <div className={styles['header']}>
          {title &&
            createElement(
              VARIANT_TO_HEADER_COMPONENT_MAPPING[variant],
              { className: classNames(styles['title'], styles['header__title']) },
              title,
            )}

          {controls && (
            <div className={classNames(styles['controls'], styles['header__controls'])}>
              {controls}
            </div>
          )}
        </div>

        <div className={classNames(styles['content'], { [styles['content--breakout']]: breakout })}>{children}</div>
      </section>
    );
  },
);

export { PageSection, type PageSectionProps };
